import React, { useState, useCallback, useRef, useEffect } from 'react';
import styles from './EnvVarsEditor.module.css';

interface EnvVarsEditorProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  /** Returns a reason string if the env var key is disallowed, null otherwise */
  checkDisallowed?: (key: string) => string | null;
}

interface EnvPill {
  id: string;
  key: string;
  value: string;
}

function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Check whether all delimiters are balanced: quotes ("" ''), braces,
 * brackets, and parens.  Prevents premature pillification when a value
 * contains JSON, paths, or nested structures.
 */
function isBalanced(text: string): boolean {
  let inDouble = false;
  let inSingle = false;
  const stack: string[] = [];
  const openers: Record<string, string> = { '{': '}', '[': ']', '(': ')' };
  const closers = new Set(Object.values(openers));

  for (const ch of text) {
    if (inSingle) { if (ch === "'") inSingle = false; continue; }
    if (inDouble) { if (ch === '"') inDouble = false; continue; }
    if (ch === '"') { inDouble = true; continue; }
    if (ch === "'") { inSingle = true; continue; }
    if (ch in openers) { stack.push(openers[ch]); continue; }
    if (closers.has(ch)) {
      if (stack.length === 0 || stack[stack.length - 1] !== ch) return false;
      stack.pop();
    }
  }
  return !inDouble && !inSingle && stack.length === 0;
}

/** Valid POSIX env var key: starts with letter or underscore, then alnum/underscore */
function isValidKey(key: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(key);
}

/**
 * Parse a single env var assignment from text.
 * Accepts: KEY=value  KEY="quoted value"  KEY=  export KEY=value
 * Returns null if not a valid assignment.
 */
function parseAssignment(text: string): { key: string; value: string } | null {
  let trimmed = text.trim();
  // Strip optional 'export ' prefix
  if (/^export\s+/i.test(trimmed)) {
    trimmed = trimmed.replace(/^export\s+/i, '');
  }
  const eqIndex = trimmed.indexOf('=');
  if (eqIndex <= 0) return null;
  const key = trimmed.substring(0, eqIndex).trim();
  if (!isValidKey(key)) return null;
  const value = trimmed.substring(eqIndex + 1);
  return { key, value };
}

/**
 * Is the input text a complete env var assignment?
 * Must have balanced quotes and a valid KEY=... form.
 */
function isComplete(text: string): boolean {
  const trimmed = text.trim();
  if (!isBalanced(trimmed)) return false;
  return parseAssignment(trimmed) !== null;
}

function makePill(key: string, value: string): EnvPill {
  return { id: generateId(), key, value };
}

/**
 * Parse a stored env string into pills.
 * Accepts (in priority order):
 *   1. JSON Record<string, string>         — {"KEY":"val"}
 *   2. JSON array of {key, value} objects  — [{"key":"K","value":"V"}]
 *   3. Legacy newline- or space-separated  — KEY=val\nKEY2=val2  or  KEY=val KEY2=val2
 */
function parseEnvString(input: string): EnvPill[] {
  if (!input.trim()) return [];

  // Try JSON first
  try {
    const parsed = JSON.parse(input);

    // Record<string, string>  e.g. {"KEY":"val"}
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return Object.entries(parsed)
        .filter(([k, v]) => typeof k === 'string' && typeof v === 'string')
        .map(([k, v]) => makePill(k, v as string));
    }

    // Array of {key, value} objects  e.g. [{"key":"K","value":"V"}]
    if (Array.isArray(parsed)) {
      const pills: EnvPill[] = [];
      for (const item of parsed) {
        if (item && typeof item === 'object' && typeof item.key === 'string' && typeof item.value === 'string') {
          pills.push(makePill(item.key, item.value));
        }
      }
      if (pills.length > 0) return pills;
    }
  } catch { /* not JSON, fall through */ }

  // Legacy text: split on newlines, then within each line split on spaces
  // (handles both "KEY=val\nKEY2=val2" and "KEY=val KEY2=val2")
  const pills: EnvPill[] = [];
  for (const line of input.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    // Tokenise the line (quote/brace-aware splitting on spaces)
    const tokens = tokeniseEnvLine(trimmed);

    // If tokenisation produces exactly one token, take it as-is
    // (single assignment, possibly with quoted/braced value containing spaces)
    if (tokens.length === 1) {
      const parsed = parseAssignment(tokens[0]);
      if (parsed) pills.push(makePill(parsed.key, parsed.value));
      continue;
    }

    // Multiple tokens — each should be its own KEY=value
    for (const token of tokens) {
      const parsed = parseAssignment(token);
      if (parsed) pills.push(makePill(parsed.key, parsed.value));
    }
  }
  return pills;
}

/**
 * Tokeniser for space-separated env var assignments on a single line.
 * Respects quotes and brace/bracket nesting so that values containing
 * JSON or nested structures are not split on inner spaces.
 */
function tokeniseEnvLine(input: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inDouble = false;
  let inSingle = false;
  let depth = 0; // brace/bracket nesting depth

  for (const ch of input) {
    if (inSingle) {
      current += ch;
      if (ch === "'") inSingle = false;
    } else if (inDouble) {
      current += ch;
      if (ch === '"') inDouble = false;
    } else if (ch === '"') {
      inDouble = true;
      current += ch;
    } else if (ch === "'") {
      inSingle = true;
      current += ch;
    } else if (ch === '{' || ch === '[' || ch === '(') {
      depth++;
      current += ch;
    } else if (ch === '}' || ch === ']' || ch === ')') {
      if (depth > 0) depth--;
      current += ch;
    } else if (/\s/.test(ch) && depth === 0) {
      if (current) { tokens.push(current); current = ''; }
    } else {
      current += ch;
    }
  }
  if (current) tokens.push(current);
  return tokens;
}

/** Serialize pills as a JSON Record<string, string> for storage. */
function serializePills(pills: EnvPill[]): string {
  if (pills.length === 0) return '';
  const obj: Record<string, string> = {};
  for (const p of pills) obj[p.key] = p.value;
  return JSON.stringify(obj);
}

/** Move an element within an array from one index to another. */
function reorder<T>(list: T[], from: number, to: number): T[] {
  const result = [...list];
  const [moved] = result.splice(from, 1);
  result.splice(to, 0, moved);
  return result;
}

// ── Component ────────────────────────────────────────────────────

export function EnvVarsEditor({ value, onChange, id, checkDisallowed }: EnvVarsEditorProps) {
  const [pills, setPills] = useState<EnvPill[]>(() => parseEnvString(value));
  const [inputValue, setInputValue] = useState('');
  const [rejection, setRejection] = useState<string | null>(null);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<string | null>(null);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Drag
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragSourceRef = useRef<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync pills from external value changes (e.g. dialog reopen with saved data)
  useEffect(() => {
    const currentSerialized = serializePills(pills);
    if (value !== currentSerialized) {
      setPills(parseEnvString(value));
      setInputValue('');
      setRejection(null);
      setSelected(new Set());
      setEditingId(null);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parent sync ──

  const updateParent = useCallback((newPills: EnvPill[]) => {
    setPills(newPills);
    onChange(serializePills(newPills));
  }, [onChange]);

  // ── Disallow check ──

  const checkKey = useCallback((key: string): boolean => {
    if (!checkDisallowed) return false;
    const reason = checkDisallowed(key);
    if (reason) {
      setRejection(`"${key}" is not allowed: ${reason}`);
      return true;
    }
    return false;
  }, [checkDisallowed]);

  // ── Selection ──

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    lastClickedRef.current = null;
  }, []);

  // ── Inline editing ──

  const startEditing = useCallback((pillId: string) => {
    const pill = pills.find(p => p.id === pillId);
    if (!pill) return;
    setEditingId(pillId);
    setEditValue(`${pill.key}=${pill.value}`);
    clearSelection();
    requestAnimationFrame(() => editInputRef.current?.focus());
  }, [pills, clearSelection]);

  const commitEdit = useCallback(() => {
    if (!editingId) return;
    const trimmed = editValue.trim();
    if (trimmed && isComplete(trimmed)) {
      const parsed = parseAssignment(trimmed);
      if (parsed) {
        if (checkKey(parsed.key)) return; // stay editing
        setRejection(null);
        const newPills = pills.map(p =>
          p.id === editingId ? { ...p, key: parsed.key, value: parsed.value } : p
        );
        updateParent(newPills);
      }
    } else if (!trimmed) {
      // Empty → delete
      updateParent(pills.filter(p => p.id !== editingId));
    }
    setEditingId(null);
    setEditValue('');
  }, [editingId, editValue, pills, checkKey, updateParent]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue('');
  }, []);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
  }, [commitEdit, cancelEdit]);

  const handleEditBlur = useCallback(() => { commitEdit(); }, [commitEdit]);

  // ── Pill click (selection) ──

  const handlePillClick = useCallback((e: React.MouseEvent, pillId: string) => {
    e.stopPropagation();
    const metaKey = e.metaKey || e.ctrlKey;

    if (e.shiftKey && lastClickedRef.current) {
      const lastIdx = pills.findIndex(p => p.id === lastClickedRef.current);
      const thisIdx = pills.findIndex(p => p.id === pillId);
      if (lastIdx >= 0 && thisIdx >= 0) {
        const lo = Math.min(lastIdx, thisIdx);
        const hi = Math.max(lastIdx, thisIdx);
        const range = new Set(metaKey ? selected : new Set<string>());
        for (let i = lo; i <= hi; i++) range.add(pills[i].id);
        setSelected(range);
      }
    } else if (metaKey) {
      const next = new Set(selected);
      if (next.has(pillId)) next.delete(pillId); else next.add(pillId);
      setSelected(next);
      lastClickedRef.current = pillId;
    } else {
      if (selected.size === 1 && selected.has(pillId)) clearSelection();
      else { setSelected(new Set([pillId])); lastClickedRef.current = pillId; }
    }
  }, [pills, selected, clearSelection]);

  // ── Copy / Cut / Paste ──

  const getSelectedText = useCallback((): string => {
    if (selected.size === 0) return '';
    return pills.filter(p => selected.has(p.id)).map(p => `${p.key}=${p.value}`).join('\n');
  }, [pills, selected]);

  const removeSelected = useCallback((): EnvPill[] => {
    const remaining = pills.filter(p => !selected.has(p.id));
    clearSelection();
    return remaining;
  }, [pills, selected, clearSelection]);

  const handleContainerKeyDown = useCallback((e: React.KeyboardEvent) => {
    const meta = e.metaKey || e.ctrlKey;
    if (!meta || selected.size === 0) return;

    if (e.key === 'c') {
      e.preventDefault();
      navigator.clipboard.writeText(getSelectedText());
    } else if (e.key === 'x') {
      e.preventDefault();
      navigator.clipboard.writeText(getSelectedText());
      updateParent(removeSelected());
    }
  }, [selected, getSelectedText, removeSelected, updateParent]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text/plain');
    if (!pasted) return;

    // If there's selected text in the input, let native paste handle it
    if (inputRef.current && inputRef.current.selectionStart !== inputRef.current.selectionEnd) return;

    // Try to parse as env vars (newline or space separated)
    const parsed: EnvPill[] = [];
    for (const line of pasted.split(/[\n\r]+/)) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;
      const assignment = parseAssignment(trimmed);
      if (!assignment) return; // Not all lines are valid — let native handle
      parsed.push(makePill(assignment.key, assignment.value));
    }

    if (parsed.length === 0) return;

    e.preventDefault();

    // Check disallow list
    for (const pill of parsed) {
      if (checkKey(pill.key)) return;
    }

    setRejection(null);
    updateParent([...pills, ...parsed]);
    clearSelection();
  }, [pills, checkKey, updateParent, clearSelection]);

  // ── Drag and drop ──

  const handleDragStart = useCallback((e: React.DragEvent, index: number, pillId: string) => {
    dragSourceRef.current = index;
    if (!selected.has(pillId)) {
      setSelected(new Set([pillId]));
      lastClickedRef.current = pillId;
    }
    const text = pills.filter(p => selected.has(p.id) || p.id === pillId)
      .map(p => `${p.key}=${p.value}`).join('\n');
    e.dataTransfer.setData('text/plain', text);
    e.dataTransfer.effectAllowed = 'copyMove';
  }, [pills, selected]);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => { setDragOverIndex(null); }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    const sourceIndex = dragSourceRef.current;
    dragSourceRef.current = null;

    if (sourceIndex === null) {
      // External drop
      const text = e.dataTransfer.getData('text/plain');
      if (!text) return;
      const parsed: EnvPill[] = [];
      for (const line of text.split(/[\n\r]+/)) {
        const a = parseAssignment(line.trim());
        if (a) parsed.push(makePill(a.key, a.value));
      }
      if (parsed.length === 0) return;
      const result = [...pills];
      result.splice(dropIndex, 0, ...parsed);
      updateParent(result);
      clearSelection();
      return;
    }

    const isCopy = e.altKey;

    if (selected.size > 1) {
      const selectedPills = pills.filter(p => selected.has(p.id));
      const remaining = pills.filter(p => !selected.has(p.id));
      if (isCopy) {
        const copies = selectedPills.map(p => makePill(p.key, p.value));
        const result = [...pills];
        result.splice(dropIndex, 0, ...copies);
        updateParent(result);
      } else {
        const countBefore = pills.slice(0, dropIndex).filter(p => selected.has(p.id)).length;
        remaining.splice(dropIndex - countBefore, 0, ...selectedPills);
        updateParent(remaining);
      }
    } else {
      if (isCopy) {
        const src = pills[sourceIndex];
        const result = [...pills];
        result.splice(dropIndex, 0, makePill(src.key, src.value));
        updateParent(result);
      } else {
        if (sourceIndex === dropIndex) return;
        updateParent(reorder(pills, sourceIndex, dropIndex));
      }
    }
    clearSelection();
  }, [pills, selected, updateParent, clearSelection]);

  const handleDragEnd = useCallback(() => {
    dragSourceRef.current = null;
    setDragOverIndex(null);
  }, []);

  // ── Pill add / remove ──

  const tryAdd = useCallback((text: string): boolean => {
    const parsed = parseAssignment(text);
    if (!parsed) return false;
    if (checkKey(parsed.key)) return false;
    setRejection(null);
    updateParent([...pills, makePill(parsed.key, parsed.value)]);
    return true;
  }, [pills, checkKey, updateParent]);

  const pillify = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || !isComplete(trimmed)) return false;
    if (!tryAdd(trimmed)) return false;
    setInputValue('');
    clearSelection();
    return true;
  }, [inputValue, tryAdd, clearSelection]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    // Space outside quotes → try to pillify
    if (val.endsWith(' ') && isBalanced(val)) {
      const trimmed = val.trim();
      if (isComplete(trimmed)) {
        if (tryAdd(trimmed)) {
          setInputValue('');
          clearSelection();
          return;
        }
        // Disallowed — keep in input
        setInputValue(val);
        return;
      }
    }

    setInputValue(val);
  }, [tryAdd, clearSelection]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Delete selected pills
    if ((e.key === 'Backspace' || e.key === 'Delete') && selected.size > 0 && !inputValue) {
      e.preventDefault();
      updateParent(pills.filter(p => !selected.has(p.id)));
      clearSelection();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      pillify();
    } else if (e.key === 'Backspace' && !inputValue) {
      if (pills.length > 0) {
        const last = pills[pills.length - 1];
        updateParent(pills.slice(0, -1));
        setInputValue(`${last.key}=${last.value}`);
        clearSelection();
      }
    } else if (e.key === 'a' && (e.metaKey || e.ctrlKey) && !inputValue) {
      e.preventDefault();
      setSelected(new Set(pills.map(p => p.id)));
    }
  }, [inputValue, pills, selected, pillify, updateParent, clearSelection]);

  const removePill = useCallback((removeId: string) => {
    updateParent(pills.filter(p => p.id !== removeId));
    clearSelection();
    inputRef.current?.focus();
  }, [pills, updateParent, clearSelection]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    if (e.target === containerRef.current || e.target === inputRef.current) {
      clearSelection();
      inputRef.current?.focus();
    }
  }, [clearSelection]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    pillify();
    clearSelection();
  }, [pillify, clearSelection]);

  // ── Render ──

  const pillClasses = (pill: EnvPill) => {
    const cls = [styles.pill];
    if (selected.has(pill.id)) cls.push(styles.pillSelected);
    return cls.join(' ');
  };

  return (
    <div>
      <div
        ref={containerRef}
        className={`${styles.container} ${rejection ? styles.containerError : ''}`}
        onClick={handleContainerClick}
        onKeyDown={handleContainerKeyDown}
        onPaste={handlePaste}
      >
        {pills.map((pill, index) => (
          editingId === pill.id ? (
            <span key={pill.id} className={`${styles.pill} ${styles.pillEditing}`}>
              <input
                ref={editInputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleEditKeyDown}
                onBlur={handleEditBlur}
                className={styles.pillEditInput}
                style={{ width: `${Math.max(editValue.length, 5)}ch` }}
                autoComplete="off"
                spellCheck={false}
              />
            </span>
          ) : (
            <span
              key={pill.id}
              className={`${pillClasses(pill)} ${dragOverIndex === index ? styles.pillDropTarget : ''}`}
              draggable
              onClick={(e) => handlePillClick(e, pill.id)}
              onDoubleClick={(e) => { e.stopPropagation(); startEditing(pill.id); }}
              onDragStart={(e) => handleDragStart(e, index, pill.id)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <span className={styles.pillKey}>{pill.key}</span>
              <span className={styles.pillEquals}>=</span>
              <span className={styles.pillValue}>{pill.value || '""'}</span>
              <button
                type="button"
                className={styles.pillRemove}
                onClick={(e) => { e.stopPropagation(); removePill(pill.id); }}
                tabIndex={-1}
              >
                ×
              </button>
            </span>
          )
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={pills.length === 0 ? 'KEY=value KEY="quoted value"' : ''}
          className={styles.input}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      {rejection && (
        <span className={styles.rejection}>{rejection}</span>
      )}
    </div>
  );
}
